import React, { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { Chat } from './Chat';
import { PaymentModal } from './PaymentModal';
import { Purchase } from '../types';

export function Dashboard() {
  const { user, token, logout } = useAuth();
  const [purchases, setPurchases] = useState<Purchase[]>([]);
  const [activePurchase, setActivePurchase] = useState<Purchase | null>(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<'starter' | 'basic' | 'pro'>('starter');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const mockPurchases: Purchase[] = [
      {
        id: 'purchase-1',
        package_type: 'starter',
        tokens_purchased: 10,
        tokens_remaining: 10,
        price_paid: 29999,
        expires_at: new Date(Date.now() + 86400000).toISOString(),
        purchased_at: new Date().toISOString(),
      },
    ];
    setPurchases(mockPurchases);
    setActivePurchase(mockPurchases[0]);
    setLoading(false);
  }, []);

  const handleBuyTokens = (pkg: 'starter' | 'basic' | 'pro') => {
    setSelectedPackage(pkg);
    setShowPaymentModal(true);
  };

  const handlePaymentComplete = (purchaseId: string) => {
    const newPurchase: Purchase = {
      id: purchaseId,
      package_type: selectedPackage,
      tokens_purchased:
        selectedPackage === 'starter' ? 10 : selectedPackage === 'basic' ? 50 : 100,
      tokens_remaining:
        selectedPackage === 'starter' ? 10 : selectedPackage === 'basic' ? 50 : 100,
      price_paid:
        selectedPackage === 'starter'
          ? 29999
          : selectedPackage === 'basic'
          ? 139999
          : 259999,
      expires_at: new Date(Date.now() + 86400000).toISOString(),
      purchased_at: new Date().toISOString(),
    };
    setPurchases([...purchases, newPurchase]);
    setActivePurchase(newPurchase);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-900 text-white">
        Loading...
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-900">
      {/* Sidebar */}
      <div className="w-72 bg-gray-800 text-white p-4 flex flex-col border-r border-gray-700 overflow-y-auto">
        <div className="mb-8">
          <h2 className="text-2xl font-bold">🔥 Claude 1 Ngày</h2>
          <p className="text-sm text-gray-400">{user?.username}</p>
          <p className="text-xs text-gray-500">{user?.email}</p>
        </div>

        {/* Token Status */}
        {activePurchase && (
          <div className="mb-6 p-4 bg-gradient-to-br from-blue-600 to-blue-700 rounded-lg">
            <div className="text-sm text-blue-100 mb-2">📊 Tokens còn lại</div>
            <div className="text-4xl font-bold text-white mb-2">{activePurchase.tokens_remaining}</div>
            <div className="text-xs text-blue-100 mb-3">
              {activePurchase.package_type.toUpperCase()} • {activePurchase.tokens_purchased}M tokens
            </div>
            <div className="w-full bg-blue-900 rounded-full h-2">
              <div
                className="bg-white h-2 rounded-full transition-all"
                style={{
                  width: `${(activePurchase.tokens_remaining / activePurchase.tokens_purchased) * 100}%`,
                }}
              ></div>
            </div>
            <div className="text-xs text-blue-100 mt-2">
              ⏰ Hết hạn: {new Date(activePurchase.expires_at).toLocaleTimeString('vi-VN')}
            </div>
          </div>
        )}

        {/* Packages */}
        <div className="mb-6">
          <div className="text-sm font-semibold mb-3 px-3">Mua tokens</div>
          <div className="space-y-2">
            <button
              onClick={() => handleBuyTokens('starter')}
              className="w-full text-left px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded transition text-sm"
            >
              <div className="font-medium">⭐ STARTER</div>
              <div className="text-xs text-gray-400">10M • 29.999đ</div>
            </button>
            <button
              onClick={() => handleBuyTokens('basic')}
              className="w-full text-left px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded transition text-sm"
            >
              <div className="font-medium">⭐⭐ BASIC</div>
              <div className="text-xs text-gray-400">50M • 139.999đ</div>
            </button>
            <button
              onClick={() => handleBuyTokens('pro')}
              className="w-full text-left px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded transition text-sm"
            >
              <div className="font-medium">⭐⭐⭐ PRO</div>
              <div className="text-xs text-gray-400">100M • 259.999đ</div>
            </button>
          </div>
        </div>

        {/* Purchased Packages */}
        {purchases.length > 0 && (
          <div className="mb-6">
            <div className="text-sm font-semibold mb-3 px-3">Packages của bạn</div>
            <div className="space-y-2">
              {purchases.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setActivePurchase(p)}
                  className={`w-full text-left px-3 py-2 rounded transition text-sm ${
                    activePurchase?.id === p.id
                      ? 'bg-blue-600'
                      : 'bg-gray-700 hover:bg-gray-600'
                  }`}
                >
                  <div className="font-medium">{p.package_type.toUpperCase()}</div>
                  <div className="text-xs text-gray-300">
                    {p.tokens_remaining}/{p.tokens_purchased}M
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="mt-auto space-y-2">
          <button className="w-full bg-gray-700 hover:bg-gray-600 text-white py-2 rounded text-sm transition">
            ⚙️ Cài đặt
          </button>
          <button
            onClick={logout}
            className="w-full bg-red-600 hover:bg-red-700 text-white py-2 rounded text-sm transition"
          >
            🚪 Đăng xuất
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col bg-gray-900">
        {activePurchase && (
          <Chat
            token={token}
            purchaseId={activePurchase.id}
            tokensRemaining={activePurchase.tokens_remaining}
            onTokensUpdate={(remaining) => {
              setActivePurchase({ ...activePurchase, tokens_remaining: remaining });
            }}
          />
        )}

        {!activePurchase && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-white">
              <p className="text-gray-400">Không có token nào</p>
              <button
                onClick={() => handleBuyTokens('starter')}
                className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded"
              >
                Mua token
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Payment Modal */}
      <PaymentModal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        packageType={selectedPackage}
        onPaymentComplete={handlePaymentComplete}
      />
    </div>
  );
}
